import type { UseQueryResult } from "@tanstack/react-query";

/**
 *  Extracts the return type of an useQuery hook.
 *
 *  Example:
 *  ```ts
 *  const useComments = () => {
 *      return useQuery(...)
 *  }
 *
 *  type res = ExtractUseQueryResult<typeof useComments>
 *  ```
 *
 *  This also works:
 *  ```ts
 *  type res = ExtractUseQueryResult<ReturnType<typeof useComments>>
 *  ```
 */
export type ExtractUseQueryResult<T, E = unknown> = T extends () => UseQueryResult<infer U, E>
  ? U
  : T extends UseQueryResult<infer U, E>
    ? U
    : never;

declare const _brand: unique symbol;

/**
 * Creates a branded type. Accepts any primitive type and a string literal.
 */
export type Brand<T, TBrand extends string> = T & { [_brand]: TBrand };

/** Builds a type safe type predicate filter function that can be used to narrow the type of an object that is of a union object type.
 * Eg. ProjectViewLinkUser = ProjectViewLinkInvitation | ProjectViewLinkMember */
export const buildTypePredicateFilter =
  <BaseType extends object, PredicateType extends BaseType>(
    key: Exclude<keyof PredicateType, keyof BaseType>
  ): ((value: BaseType) => value is PredicateType) =>
  (value): value is PredicateType =>
    key in value;

export const isNonNullable = <T>(value: T): value is NonNullable<T> =>
  value !== null && value !== undefined;

export type Modify<T, R> = Omit<T, keyof R> & {
  [P in keyof R as R[P] extends never ? never : P]: R[P];
};
